import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  clearData,
  createInitialOrder,
  fetchReferralCode,
  getJumioVerificationUrl,
  setData,
  changeFormField
} from "ducks/actions";
import {
  makeSelectIsFetchingData,
  makeSelectOrderParams,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectDeliveryDetailsItems,
  makeSelectReferralCode,
  makeSelectPaymentRequest,
  makeSelectOrderSummaryForm,
  makeSelectOrderSummaryFormInitialValues,
  makeSelectOrderSummaryPayload,
  makeSelectESigned,
  makeSelectPaymentModes,
  makeSelectFormattedSelectedNumber,
  makeSelectAddons,
  makeSelectPlanDetails,
  makeSelectDefaultPromoCodeOffers,
  makeSelectSessionToken,
  makeSelectIosDevice,
  makeSelectJumioRedirectUrl,
  makeSelectSadOrderParams,
  makeSelectOrder,
  makeSelectSimCount,
  makeSelectJumioData,
  makeSelectValidateIccId,
  makeSelectEKYCMessageData,
  makeSelectMyInfo,
  makeSelectMyInfoIsUsed,
  makeSelectMyInfoDetails,
  makeSelectJumioIsUsed,
  makeSelectGuestOrder,
  makeSelectIsWalletPromoCode,
  makeSelectEkycValidation,
  makeSelectMyInfoIsInvalid
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import forEach from "lodash/forEach";
import {
  urlParam,
  gotoUrl,
  setMyInfoFieldToClear,
  buildJumioParams,
  isUnifiedEKYCFlow,
  getChannelFromUrl
} from "utils/helpers";
import {
  SELF_ACT_DEL,
  REFERER_TYPE,
  forms,
  paths,
  JUMIO_PARAMS,
  VERIFY_IDENTITY_CHANNEL
} from "utils/constants";
import Spinner, { styles } from "components/Spinner";
import { setSessionToken } from "utils/localStorage";

const EkycHOC = Component =>
  function Comp(props) {
    const {
      isCreatingOrder,
      orderParams,
      referralCode,
      isFetchingJumio,
      clearAllMyInfoData,
      jumioData,
      jumioIsUsed,
      jumioVerificationCall,
      validateIccid,
      token,
      nextPage, // goes to jumio url
      myInfoIsUsed,
      guestOrder,
      getJumioVerificationUrl: callGetJumioVerificationUrl,
      createInitialOrder: callCreateInitialOrder,
      myInfoIsInvalid
    } = props;
    const [lastPromoCode, setLastPromoCode] = useState(
      get(referralCode, "value")
    );

    const tokenFromURL = urlParam("token");
    const [refererType, setRefererType] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (
        getChannelFromUrl() === VERIFY_IDENTITY_CHANNEL &&
        isEmpty(guestOrder) &&
        window.MANDATE_GUEST_ORDER_FOR_REDO_EKYC
      ) {
        setLoading(true);
        gotoUrl(paths.MANAGE_ORDER, true, { reset: true });
      } else {
        setLoading(false);
      }
    }, [guestOrder]);

    useEffect(() => {
      setRefererType(
        isEmpty(tokenFromURL) ? REFERER_TYPE.DEFAULT : REFERER_TYPE.DIRECT_LINK
      );
      tokenFromURL && setSessionToken(tokenFromURL);
    }, [tokenFromURL]);

    useEffect(() => {
      if (refererType === REFERER_TYPE.DIRECT_LINK && isEmpty(nextPage)) {
        jumioVerificationCall(tokenFromURL);
      }
    }, [jumioVerificationCall, refererType, tokenFromURL, nextPage]);

    useEffect(() => {
      if (!isFetchingJumio && refererType === REFERER_TYPE.DIRECT_LINK) {
        let redirectToManageOrderConditionMet =
          !isEmpty(jumioData) &&
          isEmpty(get(jumioData, JUMIO_PARAMS.TRANSACTION_REF, ""));

        if (redirectToManageOrderConditionMet) {
          props.history.push(paths.MANAGE_ORDER);
        }
      }
    }, [refererType, isFetchingJumio, jumioData, props.history]);

    useEffect(
      () => {
        // if jumio redirect url is available dont need to call this
        if (refererType === REFERER_TYPE.DEFAULT && isEmpty(nextPage)) {
          const isSadFlow = get(validateIccid, "channel") === SELF_ACT_DEL;

          const callJumio = (sessionToken, nextPath) => {
            if (sessionToken) {
              const params = buildJumioParams(
                sessionToken,
                nextPath,
                paths.EKYC
              );
              callGetJumioVerificationUrl(params);
            }
          };

          const toPath = isUnifiedEKYCFlow()
            ? paths.PERSONAL_DETAILS
            : paths.ORDER_SUMMARY;

          if (isSadFlow) {
            if (!myInfoIsUsed && !jumioIsUsed) callJumio(token, toPath);
          } else {
            if (!isEmpty(orderParams) && !token) {
              callCreateInitialOrder(orderParams, result => {
                const tokenFromRes = get(result, "session_token");
                callJumio(tokenFromRes, toPath);
              });
            } else {
              callJumio(token, toPath);
            }
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [refererType, myInfoIsUsed, jumioIsUsed]
    );

    useEffect(
      () => {
        const promoCode = get(referralCode, "value");
        if (promoCode !== lastPromoCode) {
          setLastPromoCode(promoCode);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [referralCode, lastPromoCode, setLastPromoCode]
    );

    useEffect(() => {
      if (myInfoIsInvalid && isUnifiedEKYCFlow()) {
        clearAllMyInfoData();
      }
    }, [clearAllMyInfoData, myInfoIsInvalid]);

    return isCreatingOrder || loading ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <Component {...props} />
    );
  };

const mapStateToProps = createStructuredSelector({
  formValues: makeSelectOrderSummaryForm(),
  payload: makeSelectOrderSummaryPayload(),
  order: makeSelectOrder(),
  paymentRequest: makeSelectPaymentRequest(),
  orderParams: makeSelectOrderParams(),
  sadOrderParams: makeSelectSadOrderParams(),
  isCreatingOrder: makeSelectIsFetchingData("order"),
  isIosDevice: makeSelectIosDevice(),
  nextPage: makeSelectJumioRedirectUrl(),
  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems("OrderSummary"),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  referralCode: makeSelectReferralCode(),
  eSigned: makeSelectESigned(),
  paymentModes: makeSelectPaymentModes(),
  initialValues: makeSelectOrderSummaryFormInitialValues(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  addons: makeSelectAddons(),
  planDetails: makeSelectPlanDetails(),
  token: makeSelectSessionToken(),
  sim_count: makeSelectSimCount(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  isFetchingJumio: makeSelectIsFetchingData("jumio"),
  validateIccid: makeSelectValidateIccId(),
  messageData: makeSelectEKYCMessageData(),
  myInfo: makeSelectMyInfo(),
  myInfoIsUsed: makeSelectMyInfoIsUsed(),
  myInfoDetails: makeSelectMyInfoDetails(),
  jumioIsUsed: makeSelectJumioIsUsed(),
  jumioData: makeSelectJumioData(),
  jumioRedirectUrl: makeSelectJumioRedirectUrl(),
  guestOrder: makeSelectGuestOrder(),
  isWalletPromoCode: makeSelectIsWalletPromoCode(),
  ekycValidation: makeSelectEkycValidation(),
  myInfoIsInvalid: makeSelectMyInfoIsInvalid()
});

export function mapDispatchToProps(dispatch) {
  return {
    getJumioVerificationUrl: params => {
      dispatch(getJumioVerificationUrl(params));
    },
    jumioVerificationCall: tokenFromURL => {
      if (tokenFromURL) {
        const params = buildJumioParams(
          tokenFromURL,
          paths.PERSONAL_DETAILS,
          paths.EKYC
        );
        dispatch(getJumioVerificationUrl(params));
      }
    },
    createInitialOrder: (orderParams, callback) => {
      dispatch(createInitialOrder(orderParams, callback));
    },
    validateReferralCode: (code, planName) => {
      dispatch(fetchReferralCode({ referral_code: code, plan_name: planName }));
    },
    clearMyInfoDomain: () => {
      dispatch(clearData("myInfoDetails"));
    },
    setMyInfoIsUsed: isUsed => {
      dispatch(
        setData({
          key: "myInfo",
          data: { isUsed }
        })
      );
      dispatch(
        changeFormField({
          form: forms.PERSONAL_DETAILS_FORM,
          field: "myInfoIsUsed",
          value: isUsed
        })
      );
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.PERSONAL_DETAILS_FORM,
          field: field,
          value: value
        })
      );
    },
    clearAllMyInfoData: () => {
      dispatch(clearData("myInfoDetails"));
      dispatch(clearData("myInfo"));
    },
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"]))
  };
}

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    clearMyInfoUrlParam: () => {
      gotoUrl(window.location.pathname, true, {
        code: null,
        state: null
      });
    },
    clearMyInfoFormFields: (fieldNameToClear = []) => {
      const changeField = dispatchProps.changeField;
      const fieldMap = setMyInfoFieldToClear(fieldNameToClear);
      forEach(fieldMap, fieldName => {
        changeField(fieldName, null);
      });
    }
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(withConnect, memo)(EkycHOC(SmartRender));
